import { format, parseISO } from "date-fns";
import * as XLSX from "xlsx";
import type { Content, TableCell } from "pdfmake/interfaces";
import generatePDF from "helpers/generatePDF";
import { getWithColsExportExcel } from "helpers/exportFileToExcel";
import type { DREGerencialView } from "../types/dre-gerencial.types";
import {
  formatarMoedaBRL,
  formatarPercentual,
} from "./financeiro-formatters";
import {
  buildDreGerencialLinhas,
  type DreGerencialLinha,
} from "./build-dre-gerencial-linhas";

export type DreGerencialExportPeriodo = {
  dataInicio: string;
  dataFim: string;
  granularidade?: string;
};

export type DreGerencialExportInput = {
  view: DREGerencialView;
  periodo: DreGerencialExportPeriodo;
};

function formatarDataYmd(ymd: string): string {
  try {
    return format(parseISO(ymd), "dd/MM/yyyy");
  } catch {
    return ymd;
  }
}

function exportFilenameBase(periodo: DreGerencialExportPeriodo): string {
  return `DRE-gerencial_${periodo.dataInicio}_${periodo.dataFim}`;
}

function periodoLabel(periodo: DreGerencialExportPeriodo): string {
  const ini = formatarDataYmd(periodo.dataInicio);
  const fim = formatarDataYmd(periodo.dataFim);
  const gran = periodo.granularidade
    ? ` · granularidade ${periodo.granularidade}`
    : "";
  return `${ini} a ${fim}${gran}`;
}

function buildResumoItems(view: DREGerencialView) {
  return [
    { label: "Receita líquida", valor: view.receitaLiquida, percentual: false },
    { label: "CMV", valor: view.cmvTotal, percentual: false },
    { label: "Lucro bruto", valor: view.lucroBruto, percentual: false },
    { label: "Margem bruta", valor: view.margemBruta, percentual: true },
    { label: "Despesas operacionais", valor: view.despesasTotal, percentual: false },
    { label: "Lucro operacional", valor: view.lucroOperacional, percentual: false },
  ];
}

function formatResumoValor(valor: number, percentual: boolean): string {
  return percentual ? formatarPercentual(valor) : formatarMoedaBRL(valor);
}

function contaPdfLabel(linha: DreGerencialLinha): string {
  return linha.nivel === "sub" ? `    ${linha.conta}` : linha.conta;
}

function pdfCellValor(linha: DreGerencialLinha): TableCell {
  const bold =
    linha.variant === "highlight" ||
    linha.variant === "total" ||
    linha.variant === "main";
  const color = linha.valorCorErro
    ? "#c62828"
    : linha.valorCorSucesso
      ? "#2e7d32"
      : undefined;

  return {
    text: linha.valorFormatado,
    alignment: "right",
    bold,
    color,
    fontSize: linha.variant === "sub" ? 9 : 10,
  };
}

function buildQualidadePdfContent(view: DREGerencialView): Content[] {
  const q = view.qualidadeDados;
  if (!q || q.pedidosReceitaSemCmvSnapshot <= 0) return [];

  return [
    {
      text: "Qualidade dos dados",
      fontSize: 11,
      bold: true,
      margin: [0, 16, 0, 6],
    },
    {
      text: `${q.pedidosReceitaSemCmvSnapshot} pedido(s) com receita no período ainda sem snapshot de CMV — o lucro bruto pode estar superestimado.`,
      fontSize: 9,
      color: "#e65100",
      margin: [0, 0, 0, 4],
    },
    {
      ul: [
        `Pedidos com receita no período: ${q.pedidosComReceitaNoPeriodo}`,
        `Pedidos com snapshot de CMV: ${q.pedidosComCmvSnapshot}`,
        `Pedidos com receita sem CMV: ${q.pedidosReceitaSemCmvSnapshot}`,
      ],
      fontSize: 9,
    },
  ];
}

export async function exportDreGerencialPdf({
  view,
  periodo,
}: DreGerencialExportInput): Promise<void> {
  const linhas = buildDreGerencialLinhas(view);
  const resumo = buildResumoItems(view);
  const emitidoEm = format(new Date(), "dd/MM/yyyy HH:mm");

  const resumoBody: TableCell[][] = [
    [
      { text: "Indicador", bold: true, fillColor: "#eeeeee", fontSize: 9 },
      { text: "Valor", bold: true, fillColor: "#eeeeee", fontSize: 9, alignment: "right" },
    ],
    ...resumo.map((item) => [
      { text: item.label, fontSize: 9 },
      {
        text: formatResumoValor(item.valor, item.percentual),
        fontSize: 9,
        alignment: "right",
        bold: item.label === "Lucro operacional",
      },
    ]),
  ];

  const dreBody: TableCell[][] = [
    [
      { text: "Conta", bold: true, fillColor: "#eeeeee", fontSize: 9 },
      { text: "Valor", bold: true, fillColor: "#eeeeee", fontSize: 9, alignment: "right" },
    ],
    ...linhas.map((linha) => [
      {
        text: contaPdfLabel(linha),
        fontSize: linha.variant === "sub" ? 9 : 10,
        bold: linha.variant === "highlight" || linha.variant === "total",
      },
      pdfCellValor(linha),
    ]),
  ];

  const content: Content[] = [
    {
      text: "DRE Gerencial",
      fontSize: 16,
      bold: true,
      alignment: "center",
      margin: [0, 0, 0, 4],
    },
    {
      text: `Período: ${periodoLabel(periodo)}`,
      fontSize: 10,
      alignment: "center",
      color: "#555555",
      margin: [0, 0, 0, 2],
    },
    {
      text: `Emitido em ${emitidoEm}`,
      fontSize: 8,
      alignment: "center",
      color: "#888888",
      margin: [0, 0, 0, 16],
    },
    {
      text: "Resumo",
      fontSize: 12,
      bold: true,
      margin: [0, 0, 0, 6],
    },
    {
      table: { widths: ["*", 100], body: resumoBody },
      layout: "lightHorizontalLines",
      margin: [0, 0, 0, 16],
    },
    {
      text: "Estrutura da DRE",
      fontSize: 12,
      bold: true,
      margin: [0, 0, 0, 6],
    },
    {
      table: { widths: ["*", 100], body: dreBody },
      layout: {
        fillColor: (rowIndex: number) =>
          rowIndex === 0 ? "#eeeeee" : rowIndex % 2 === 0 ? "#fafafa" : null,
      },
    },
    ...buildQualidadePdfContent(view),
  ];

  await generatePDF({
    filename: exportFilenameBase(periodo),
    content,
    header: {
      text: "TNV Team Sports — Financeiro",
      alignment: "center",
      margin: [0, 16, 0, 0],
      fontSize: 9,
      color: "#666666",
    },
    footer: (currentPage, pageCount) => ({
      text: `Página ${currentPage} de ${pageCount}`,
      alignment: "center",
      fontSize: 8,
      color: "#888888",
      margin: [0, 8, 0, 0],
    }),
  });
}

export function exportDreGerencialExcel({
  view,
  periodo,
}: DreGerencialExportInput): void {
  const linhas = buildDreGerencialLinhas(view);
  const resumo = buildResumoItems(view);
  const wb = XLSX.utils.book_new();
  const emitidoEm = format(new Date(), "dd/MM/yyyy HH:mm");

  const wsResumo = XLSX.utils.aoa_to_sheet([
    ["DRE Gerencial"],
    [`Período: ${periodoLabel(periodo)}`],
    [`Emitido em: ${emitidoEm}`],
    [],
    ["Indicador", "Valor"],
    ...resumo.map((item) => [
      item.label,
      item.percentual ? item.valor : item.valor,
    ]),
  ]);

  const pctFmtRows = resumo
    .map((item, idx) => (item.percentual ? idx + 5 : -1))
    .filter((i) => i >= 0);
  for (const rowIdx of pctFmtRows) {
    const cell = wsResumo[XLSX.utils.encode_cell({ r: rowIdx, c: 1 })];
    if (cell) cell.z = "0.00%";
  }

  wsResumo["!cols"] = [{ wch: 28 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");

  const dreRows = linhas.map((linha) => ({
    conta: linha.conta,
    valor: linha.isPercentual ? linha.valorNumerico ?? 0 : linha.valorNumerico ?? 0,
    nivel: linha.nivel === "sub" ? "Sub" : "Principal",
    tipo: linha.isPercentual ? "Percentual" : "Moeda",
  }));

  const headerDre = ["Conta", "Valor (R$ ou %)", "Nível", "Tipo"];
  const wsDre = XLSX.utils.json_to_sheet([]);
  XLSX.utils.sheet_add_aoa(wsDre, [headerDre], { origin: "A1" });
  XLSX.utils.sheet_add_json(wsDre, dreRows, { origin: "A2", skipHeader: true });
  wsDre["!cols"] = getWithColsExportExcel(headerDre, dreRows);
  XLSX.utils.book_append_sheet(wb, wsDre, "DRE");

  const q = view.qualidadeDados;
  if (q && q.pedidosReceitaSemCmvSnapshot > 0) {
    const wsQual = XLSX.utils.aoa_to_sheet([
      ["Qualidade dos dados"],
      [],
      ["Métrica", "Quantidade"],
      ["Pedidos com receita no período", q.pedidosComReceitaNoPeriodo],
      ["Pedidos com snapshot de CMV", q.pedidosComCmvSnapshot],
      ["Pedidos com receita sem CMV", q.pedidosReceitaSemCmvSnapshot],
      [],
      [
        "Observação",
        "Pedidos sem snapshot de CMV podem superestimar o lucro bruto.",
      ],
    ]);
    wsQual["!cols"] = [{ wch: 36 }, { wch: 48 }];
    XLSX.utils.book_append_sheet(wb, wsQual, "Qualidade");
  }

  XLSX.writeFile(wb, `${exportFilenameBase(periodo)}.xlsx`, {
    compression: true,
  });
}
